import {
  API_PATHS,
  USE_MOCK,
} from '../config';
import { request, uploadWithProgress } from './client';
import { mockUploadVideo } from '../mock/uploadMock';
import { unwrapApiData } from '../utils/apiResponse';

let cachedUploadMode = null;

async function resolveUploadMode() {
  if (cachedUploadMode) {
    return cachedUploadMode;
  }
  try {
    const payload = await request(API_PATHS.health);
    const data = unwrapApiData(payload);
    cachedUploadMode = data.uploadMode === 'presign' ? 'presign' : 'chunk';
  } catch (e) {
    cachedUploadMode = 'chunk';
  }
  return cachedUploadMode;
}

async function uploadViaChunk(file, onProgress) {
  const initPayload = await request(API_PATHS.uploadInit, { method: 'POST' });
  const initData = unwrapApiData(initPayload);
  const chunkSize = initData.chunkSize || 8 * 1024 * 1024;
  const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));

  for (let index = 0; index < totalChunks; index += 1) {
    const start = index * chunkSize;
    const end = Math.min(file.size, start + chunkSize);
    const chunkBlob = file.slice(start, end);
    const formData = new FormData();
    formData.append('uploadId', initData.uploadId);
    formData.append('chunkIndex', String(index));
    formData.append('chunk', chunkBlob, `${file.name}.part${index}`);

    await uploadWithProgress(API_PATHS.uploadChunk, formData);
    if (onProgress) {
      onProgress({ percent: Math.round(((index + 1) / totalChunks) * 100) });
    }
  }

  const completePayload = await request(API_PATHS.uploadComplete, {
    method: 'POST',
    body: {
      uploadId: initData.uploadId,
      fileName: file.name,
      totalChunks,
      contentType: file.type || 'video/mp4',
    },
  });
  return unwrapApiData(completePayload);
}

function buildPresignUploadError(xhr, uploadUrl) {
  let host = '';
  try {
    host = new URL(uploadUrl).host;
  } catch (e) {
    host = String(uploadUrl || '');
  }
  if (host.includes('-internal')) {
    return new Error(
      'OSS 直传失败：预签名 URL 指向内网地址，请检查 ECS 环境变量 ALIYUN_OSS_PUBLIC_ENDPOINT（须为公网 Endpoint）',
    );
  }
  if (String(uploadUrl).startsWith('http://')) {
    return new Error(
      'OSS 直传失败：预签名 URL 为 http，HTTPS 站点会被浏览器拦截（混合内容）。请升级后端 jar（公网 OSS 客户端已强制 HTTPS）并重启 Java。',
    );
  }
  if (xhr && xhr.status) {
    return new Error(`OSS 直传失败: HTTP ${xhr.status}（请检查 OSS CORS 与 Content-Type 是否与 presign 一致）`);
  }
  return new Error(
    'OSS 直传网络错误：请在 OSS 控制台 Bucket 跨域规则中添加来源 '
    + '(与 /api/health 返回的 ossCorsOriginHint 一致，如 https://lingcaiai.luogt.xyz)。'
    + ' 详见项目 docs/deploy/OSS跨域配置.md',
  );
}

async function uploadViaPresign(file, onProgress) {
  const contentType = file.type || 'video/mp4';
  const query = new URLSearchParams({
    fileName: file.name,
    contentType,
    fileSize: String(file.size),
  });
  const presignPayload = await request(`${API_PATHS.uploadPresign}?${query.toString()}`);
  const presignData = unwrapApiData(presignPayload);
  const uploadContentType = presignData.contentType || contentType;
  const uploadUrl = presignData.uploadUrl;

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(presignData.method || 'PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', uploadContentType);
    xhr.upload.onprogress = (evt) => {
      if (onProgress && evt.lengthComputable) {
        onProgress({ percent: Math.round((evt.loaded / evt.total) * 100) });
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(buildPresignUploadError(xhr, uploadUrl));
      }
    };
    xhr.onerror = () => reject(buildPresignUploadError(xhr, uploadUrl));
    xhr.send(file);
  });

  const completePayload = await request(API_PATHS.uploadPresignComplete, {
    method: 'POST',
    body: {
      projectId: presignData.projectId,
      objectKey: presignData.objectKey,
      fileName: file.name,
      contentType,
      sizeBytes: file.size,
    },
  });
  return unwrapApiData(completePayload);
}

export async function uploadVideoFile(file, onProgress) {
  if (USE_MOCK) {
    return mockUploadVideo(file, onProgress);
  }

  const mode = await resolveUploadMode();
  if (mode === 'presign') {
    return uploadViaPresign(file, onProgress);
  }
  return uploadViaChunk(file, onProgress);
}
